import { useFormField } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { ContextualBanner } from '@calendly/ui/components/contextual-banner';
import { AlertWarningCircleIcon, ArrowExpandIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import { colorTextAction } from '@calendly/ui/theme';
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import { useState, forwardRef } from 'react';
import { useGetUserQuery } from 'common';
import { TYPE_INCLUSIVE_STRING } from './constants';
import { Rule } from './rule';
import { RuleField } from './rule_field';
import { useFetchDoubleBookingSuggestionsQuery, useFetchUserCalendarAccountsConnectedQuery } from '../../../../../api';
import { AddButton } from '../../../../components';
import { useCalendarSettingsUrl } from '../../../hooks';
const containerCss = "cbnthy5";
const Container = forwardRef((props, ref) => <div ref={ref} {...props} className={classNames(containerCss, props.className)} />);
const warningTextIsOwnMemberCss = "wosb3tu";
const warningTextCss = "w1yuokl3";
const WarningText = forwardRef((props, ref) => {
  const {
    isOwnMember,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'p';
  return <Component ref={ref} {...restProps} className={classNames(warningTextCss, {
    [warningTextIsOwnMemberCss]: isOwnMember
  }, props.className)} />;
});
const connectedCalendarsLinkCss = "c17ytm6s";
const ConnectedCalendarsLink = forwardRef((props, ref) => {
  const href = useCalendarSettingsUrl();
  const target = '_blank';
  const rel = 'noopener noreferrer';
  const underline = false;
  return <Link ref={ref} {...props} href={href} target={target} rel={rel} underline={underline} className={classNames(connectedCalendarsLinkCss, props.className)}>
      {props.children}
      <ArrowExpandIcon inline color={colorTextAction} spacingLeft="4" />
    </Link>;
});
const NoCalendarWarningBanner = ({
  isOwnMember
}) => <ContextualBanner icon={AlertWarningCircleIcon} variant="warning">
    <WarningText isOwnMember={isOwnMember}>
      {isOwnMember ? I18n.t('publisher.event_type_editor.sections.available_hours.bookable_events.no_calendars_connected_warning') : I18n.t('publisher.event_type_editor.sections.available_hours.bookable_events.no_calendars_connected_warning_other_host')}
    </WarningText>
    {isOwnMember && <ConnectedCalendarsLink>
        {I18n.t('publisher.event_type_editor.sections.available_hours.bookable_events.connect_calendar')}
      </ConnectedCalendarsLink>}
  </ContextualBanner>;
const withoutDuplicates = (rules, rule) => {
  const {
    searchTerm,
    type,
    id
  } = rule;
  const ruleDuplicates = rules.filter(r => searchTerm === r.searchTerm && type === r.type && !r._destroy);
  return ruleDuplicates.length > 1 ? rules.filter(r => r.id !== id) : rules;
};
export const MeetingExceptions = ({
  doubleBookingRuleMembership,
  doubleBookingRuleMembershipIndex
}) => {
  const {
    data: currentUser
  } = useGetUserQuery();
  const isOwnMember = doubleBookingRuleMembership?.userUuid === currentUser.uuid;
  const {
    data: calendarAccounts
  } = useFetchUserCalendarAccountsConnectedQuery(doubleBookingRuleMembership?.userUuid, {
    skip: !doubleBookingRuleMembership || doubleBookingRuleMembership.isPending
  });
  const hasCalendarAccounts = calendarAccounts && calendarAccounts.connected;
  const hasNoCalendarAccounts = calendarAccounts && !calendarAccounts.connected;
  const [{
    value: doubleBookingRules
  },, {
    setValue
  }] = useFormField({
    name: `doubleBookingRuleMemberships.${doubleBookingRuleMembershipIndex}.doubleBookingRules`
  });
  const [editingRuleId, setEditingRuleId] = useState(null);
  const shouldFetch = isOwnMember && hasCalendarAccounts;
  const showSearchStatus = isOwnMember && hasCalendarAccounts;

  // Use RTK Query hook for double booking suggestions
  const {
    data: doubleBookingSuggestions,
    isLoading: doubleBookingSearching,
    error: doubleBookingSearchError
  } = useFetchDoubleBookingSuggestionsQuery(undefined, {
    skip: !shouldFetch
  });
  const handleAdd = () => {
    const newId = nanoid(4);
    setValue([...doubleBookingRules, {
      id: newId,
      searchTerm: '',
      type: TYPE_INCLUSIVE_STRING
    }]);
    setEditingRuleId(newId);
  };
  const handleEdit = id => {
    setValue(doubleBookingRules.filter(({
      searchTerm
    }) => searchTerm));
    setEditingRuleId(id);
  };
  const handleClose = () => {
    setValue(doubleBookingRules.filter(({
      searchTerm
    }) => searchTerm));
    setEditingRuleId(null);
  };
  const handleRemove = id => {
    const rules = doubleBookingRules.map(rule => rule.id === id ? {
      ...rule,
      _destroy: true
    } : rule).filter(({
      searchTerm
    }) => searchTerm);
    setValue(rules);
    setEditingRuleId(null);
  };
  const handleSave = editingRule => {
    const rules = doubleBookingRules.map(rule => rule.id === editingRule.id ? editingRule : rule).filter(({
      searchTerm
    }) => searchTerm);
    const uniqRules = withoutDuplicates(rules, editingRule);
    setValue(uniqRules);
    setEditingRuleId(null);
  };
  return <Container>
      {hasNoCalendarAccounts && <NoCalendarWarningBanner isOwnMember={isOwnMember} />}
      <Container>
        {doubleBookingRules.filter(rule => {
        if (rule._destroy) return false;
        const hasName = (rule.searchTerm ?? '').trim().length > 0;
        const isEditingThis = rule.id === editingRuleId;
        return hasName || isEditingThis;
      }).map(rule => rule.id === editingRuleId ? <RuleField key={rule.id} rule={rule} onSubmit={handleSave} onClose={handleClose} showSearchStatus={showSearchStatus} doubleBookingSuggestions={doubleBookingSuggestions} doubleBookingSearching={doubleBookingSearching} doubleBookingSearchError={doubleBookingSearchError} /> : <Rule key={rule.id} rule={rule} onEdit={handleEdit} onRemove={handleRemove} />)}
      </Container>
      <AddButton onClick={handleAdd}>
        {I18n.t('publisher.event_type_editor.sections.available_hours.bookable_events.add_meeting_exception.link_label')}
      </AddButton>
    </Container>;
};

require("./meeting_exceptions.wyw-in-js.css!=!../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./meeting_exceptions.js");